import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styles from "./Preparations.module.scss";
import RightIcon from "../../../../assets/icons/chewron_right.svg?react";
import PlusIcon from "../../../../assets/icons/plus_circle.svg?react";
import CloseIcon from "../../../../assets/icons/close.svg?react";
import IconPDF from "../../../../assets/icons/content_type_pdf.svg?react";
import IconAudio from "../../../../assets/icons/content_type_audio.svg?react";
import IconSlide from "../../../../assets/icons/content_type_slide.svg?react";
import IconVideo from "../../../../assets/icons/content_type_video.svg?react";
import IconForm from "../../../../assets/icons/content_type_feedback.svg?react";
import SlideIcon from "@assets/icons/content_type_slide_com.svg?react";

import { Content, ContentType } from "@types";
import SectionContents from "../lesson/lesson-section/section-contents/TeacherContents";
import LocalPopup from "src/components/local-popup/LocalPopup";
import { useEditUnit } from "../edit/useEditUnit";
import {
  useCreateContentMutation,
  ContentFragmentDoc,
  LessonType,
  HeaModuleFragment as HeaModule,
  HeaModuleFragmentDoc,
  useHeaCreateModuleMutation,
} from "@api/graphql-codegen";
import Spinner from "@components/spinner/Spinner";
import { logger } from "../../../../utils/logger";

type Props = {
  title: string;
  contents?: Content[];
  heaModules?: HeaModule[];
  lessonType: LessonType | undefined;
  lessonOpened: boolean;
  isHEPreparation?: boolean;
  materialContents?: Content[];
};

function Preparations(props: Props) {
  const {
    title,
    contents = [],
    heaModules = [],
    lessonType,
    lessonOpened,
    isHEPreparation,
    materialContents = [],
  } = props;
  const {
    setContent,
    unit,
    heaUnit,
    setLesson,
    setSelectionLevel,
    setIsPreparation,
  } = useEditUnit();

  const [opened, setOpened] = useState(true);
  const [createPopupOpened, setCreatePopupOpened] = useState(false);
  const [matModulePopupOpened, setMatModulePopupOpened] = useState(false);

  const [createNewContent, { loading }] = useCreateContentMutation({
    update(cache, res) {
      const data = res.data?.createContent as Content;
      if (data) {
        const newContentRef = cache.writeFragment({
          data,
          fragment: ContentFragmentDoc,
        });
        if (lessonType === LessonType.PREPARATION) {
          cache.modify({
            id: `Unit:${unit?.id}`,
            fields: {
              teacherPreparation(existingRefs = []) {
                return [...existingRefs, newContentRef];
              },
            },
          });
        } else if (lessonType === LessonType.ALTERNATE) {
          cache.modify({
            id: `Unit:${unit?.id}`,
            fields: {
              alternateMaterials(existingRefs = []) {
                return [...existingRefs, newContentRef];
              },
            },
          });
        } else if (lessonType === LessonType.STUDYGUIDE) {
          if (createPopupOpened) {
            cache.modify({
              id: `Unit:${unit?.id}`,
              fields: {
                studyGuides(existingRefs = []) {
                  return [...existingRefs, newContentRef];
                },
              },
            });
          } else if (matModulePopupOpened) {
            cache.modify({
              id: `Unit:${unit?.id}`,
              fields: {
                studyGuideMaterials(existingRefs = []) {
                  return [...existingRefs, newContentRef];
                },
              },
            });
          }
        } else if (lessonType === LessonType.STUDENTQUIZ) {
          cache.modify({
            id: `Unit:${unit?.id}`,
            fields: {
              studentQuizzes(existingRefs = []) {
                return [...existingRefs, newContentRef];
              },
            },
          });
        }

        setContent(data);
        setSelectionLevel("Content");
        setCreatePopupOpened(false);
        setMatModulePopupOpened(false);
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      alert("Something went wrong.");
      setCreatePopupOpened(false);
      setMatModulePopupOpened(false);
    },
  });

  const [heaCreateModule, { loading: isHeaModuleLoading }] =
    useHeaCreateModuleMutation({
      update(cache, res) {
        const data = res.data?.heaCreateModule;
        if (data && heaUnit?.id) {
          const newContentRef = cache.writeFragment({
            data,
            fragment: HeaModuleFragmentDoc,
          });
          cache.modify({
            id: `HEAUnit:${heaUnit?.id}`,
            fields: {
              parentPreparation(existingRefs = []) {
                return [...existingRefs, newContentRef];
              },
            },
          });
          const newContent: Content = {
            ...data,
            __typename: "Content",
          };
          setIsPreparation(true);
          setContent(newContent);
          setSelectionLevel("Content");
        }
      },
      onError(e) {
        logger.debug(`[e]`, e);
        alert("Something went wrong.");
      },
    });

  const createContent = useCallback(
    (contentType: ContentType) => () => {
      if (!isHEPreparation && unit?.id) {
        const nextPosition =
          contents.reduce((acc, c) => {
            if (c.position && c.position! > acc) return c.position;
            return acc;
          }, 0) + 1;
        createNewContent({
          variables: {
            input: {
              lessonId: unit.id,
              type: contentType,
              lessonType: lessonType,
              position: nextPosition,
            },
          },
        });
      } else if (heaUnit?.id) {
        const nextPosition =
          heaModules.reduce((acc, c) => {
            if (c.position && c.position! > acc) return c.position;
            return acc;
          }, 0) + 1;
        heaCreateModule({
          variables: {
            input: {
              unitId: heaUnit?.id,
              type: contentType,
              position: nextPosition,
            },
          },
        });
      }
      // handleCloseCreatePopup();
    },
    [
      isHEPreparation,
      unit?.id,
      heaUnit?.id,
      contents,
      createNewContent,
      lessonType,
      heaModules,
      heaCreateModule,
    ],
  );

  const createMaterialContent = useCallback(
    (contentType: ContentType) => () => {
      if (unit?.id) {
        const nextPosition =
          materialContents.reduce((acc: any, c) => {
            if (c.position! > acc) return c.position;
            return acc;
          }, 0) + 1;
        createNewContent({
          variables: {
            input: {
              lessonId: unit.id,
              type: contentType,
              lessonType: LessonType.STUDYGUIDEMATERIAL,
              position: nextPosition,
            },
          },
        });
      }
    },
    [materialContents, createNewContent, unit?.id],
  );

  useEffect(() => {
    if (lessonOpened) setOpened(true);
  }, [lessonOpened]);

  useEffect(() => {
    setLesson(undefined);
  }, [setLesson]);

  const handleToggle = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const _opened = !opened;
    setOpened(_opened);
  };

  const wrapperClassName = useMemo(() => {
    const classes = [styles.wrapper];
    if (opened) {
      classes.push(styles.opened);
    }

    return classes.join(" ");
  }, [opened]);

  const handleShowCreatePopup = () => {
    setCreatePopupOpened(true);
  };

  const handleCloseCreatePopup = () => {
    setCreatePopupOpened(false);
  };

  const handleShowMatPopup = () => {
    setMatModulePopupOpened(true);
  };

  const handleCloseMatPopup = () => {
    setMatModulePopupOpened(false);
  };

  const popupContent = useMemo(() => {
    return (
      <div className={styles.popup}>
        <div className={styles.popupHeader}>
          <div className={styles.popupTitle}>Add content</div>
          <div
            className={styles.popupCloseWrapper}
            onClick={handleCloseCreatePopup}
          >
            <CloseIcon />
          </div>
        </div>
        {loading || isHeaModuleLoading ? (
          <div className={styles.popupLoading}>
            <Spinner width={20} height={20} />
          </div>
        ) : (
          <div>
            <div className={styles.popupContent}>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.VIDEO)}
              >
                <IconVideo />
                <div className={styles.itemName}>Video</div>
              </div>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.AUDIO)}
              >
                <IconAudio />
                <div className={styles.itemName}>Audio</div>
              </div>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.SLIDE)}
              >
                <IconSlide />
                <div className={styles.itemName}>Slides</div>
              </div>
            </div>
            <div className={styles.popupContent}>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.PDF)}
              >
                <IconPDF />
                <div className={styles.itemName}>PDF</div>
              </div>
              <div
                className={styles.popupItemForm}
                onClick={createContent(ContentType.FORM)}
              >
                <IconForm />
                <div className={styles.itemName}>Form</div>
              </div>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.PARENT)}
              >
                <div>PS</div>
                <div className={styles.itemName}>Parent</div>
              </div>
            </div>
            <div className={styles.popupContent}>
              <div
                className={styles.popupItem}
                onClick={createContent(ContentType.ZIPSLIDE)}
              >
                <SlideIcon />
                <div className={styles.itemName}>Zip Slides</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [createContent, loading, isHeaModuleLoading]);

  const materialPopupContent = useMemo(() => {
    return (
      <div className={styles.matPopup}>
        <div className={styles.popupHeader}>
          <div className={styles.popupTitle}>Add content</div>
          <div
            className={styles.popupCloseWrapper}
            onClick={handleCloseMatPopup}
          >
            <CloseIcon />
          </div>
        </div>
        {loading || isHeaModuleLoading ? (
          <div className={styles.popupLoading}>
            <Spinner width={20} height={20} />
          </div>
        ) : (
          <div>
            <div className={styles.popupContent}>
              <div
                className={styles.popupItem}
                onClick={createMaterialContent(ContentType.PDF)}
              >
                <IconPDF />
                <div className={styles.itemName}>PDF</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [createMaterialContent, loading, isHeaModuleLoading]);

  const contentsArray: Content[] = useMemo(() => {
    if (!isHEPreparation) {
      return contents;
    }
    return heaModules;
  }, [contents, heaModules, isHEPreparation]);

  return (
    <div className={wrapperClassName}>
      <div onClick={handleToggle} className={styles.header}>
        <RightIcon className={styles.rightIcon} />
        <div className={styles.title}>{title}</div>
      </div>
      <div className={styles.content}>
        <SectionContents
          contents={contentsArray}
          isHEPreparation={isHEPreparation}
          lessonType={lessonType}
        />
        <LocalPopup
          content={popupContent}
          open={createPopupOpened}
          onClose={handleCloseCreatePopup}
        >
          <div
            className={styles.addContentWrapper}
            onClick={handleShowCreatePopup}
          >
            <PlusIcon className={styles.addButtonIcon} />
            <div className={styles.addButtonTitle}>Add new module</div>
            {(loading || isHeaModuleLoading) && createPopupOpened ? (
              <div className={styles.loading}>
                <Spinner width={20} height={20} />
              </div>
            ) : null}
          </div>
        </LocalPopup>
        {lessonType === LessonType.STUDYGUIDE && (
          <>
            <SectionContents
              contents={materialContents}
              isHEPreparation={isHEPreparation}
              lessonType={lessonType}
              isSupportingMaterial={true}
            />
            <LocalPopup
              content={materialPopupContent}
              open={matModulePopupOpened}
              onClose={handleCloseMatPopup}
            >
              <div
                className={styles.addContentWrapper}
                onClick={handleShowMatPopup}
              >
                <PlusIcon className={styles.addButtonIcon} />
                <div className={styles.addButtonTitle}>
                  Add Supporting Material
                </div>
                {(loading || isHeaModuleLoading) && matModulePopupOpened ? (
                  <div className={styles.loading}>
                    <Spinner width={20} height={20} />
                  </div>
                ) : null}
              </div>
            </LocalPopup>
          </>
        )}
      </div>
    </div>
  );
}

export default React.memo(Preparations);
